import React from "react";
import type { ColumnDef } from "@tanstack/react-table";

import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { DataTableShell } from "@/components/ui/data-table-shell";
import type { RawFrameRow } from "@/lib/telemetry";
import { formatNumber, formatTs, type TelemetryTz } from "@/components/telemetry/format";
import { useCatalog } from "@/app/localization";

export function rawFrameRef(frame: RawFrameRow): string | null {
  if (!frame.src_file || frame.src_line == null) return null;
  return `${frame.src_file}:${frame.src_line}`;
}

/**
 * The archived frames themselves, one row per logged UDP exchange.
 *
 * The decoded table above answers "what happened"; this one answers "what was
 * actually on the wire", which is the question support falls back to when the
 * decode looks wrong. It is a different reading of the same rows, not a
 * different window — so it shows what `/raw-window` returns and derives
 * nothing: no merging, no event kinds, no severity colour. A frame here is
 * whatever the charger sent, including the ones the decoder made nothing of.
 */
export function RawFramesTable({
  frames,
  tz,
  isLoading,
  hasMore,
  isLoadingMore,
  onLoadMore,
}: {
  frames: RawFrameRow[];
  tz: TelemetryTz;
  isLoading?: boolean;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
}) {
  const { t, catalog } = useCatalog();
  const columns = React.useMemo<ColumnDef<RawFrameRow, any>[]>(
    () => [
      {
        id: "ts",
        header: t("operator.telemetry.column_time", { zone: tz }),
        cell: ({ row }) => (
          <span className="font-mono text-xs whitespace-nowrap">
            {formatTs(row.original.ts_utc, tz)}
          </span>
        ),
      },
      {
        id: "state",
        header: t("operator.telemetry.column_state"),
        cell: ({ row }) => <span className="text-xs">{row.original.state ?? "—"}</span>,
      },
      {
        id: "readings",
        header: t("operator.telemetry.column_readings_frequency"),
        cell: ({ row }) => (
          <span className="font-mono text-xs whitespace-nowrap">
            {formatNumber(row.original.voltage)} / {formatNumber(row.original.current)} /{" "}
            {row.original.temperature ?? "—"} / {formatNumber(row.original.frequency)}
          </span>
        ),
      },
      {
        id: "command",
        header: t("operator.telemetry.column_server_response"),
        cell: ({ row }) => (
          <span className="font-mono text-xs break-all">{row.original.command || "—"}</span>
        ),
      },
      {
        id: "raw",
        header: t("operator.telemetry.column_raw_frame"),
        cell: ({ row }) => (
          <span className="block max-w-[46ch] break-all whitespace-pre-wrap font-mono text-[11px]">
            {row.original.raw_message ?? "—"}
          </span>
        ),
      },
      {
        id: "ref",
        header: t("operator.telemetry.column_ref"),
        cell: ({ row }) => (
          <span className="font-mono text-[11px] text-muted-foreground">
            {rawFrameRef(row.original) ?? "—"}
          </span>
        ),
      },
    ],
    [tz, t, catalog],
  );

  return (
    <DataTableShell
      height="auto"
      minHeight="0"
      className="[&>div]:max-h-[480px]"
      footer={
        <>
          <span className="text-xs text-muted-foreground">
            {t("operator.telemetry.raw_frames_count", { count: frames.length })}
          </span>
          {hasMore && onLoadMore && (
            <Button
              variant="outline"
              size="sm"
              onClick={onLoadMore}
              disabled={isLoadingMore}
              data-testid="telemetry-raw-load-more"
            >
              {isLoadingMore ? t("common.state.loading") : t("operator.telemetry.raw_load_more")}
            </Button>
          )}
        </>
      }
    >
      <DataTable
        columns={columns}
        data={frames}
        isLoading={isLoading}
        tableClassName="text-xs"
        emptyMessage={t("operator.telemetry.frames_empty")}
      />
    </DataTableShell>
  );
}
